import { type ComponentProps, type ReactNode, type RefObject, useId } from 'react';
import { AdornedInput, Box, CloseButton, Modal } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import { MapPin } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useModalDismissProps } from '@/src/lib/hooks/ui/useModalDismissProps';
import { locationPillSurface } from '@/src/components/ListingSearch/locationPill.styles';

type Props = {
  title: string;
  input: ReactNode;
  inputRef: RefObject<HTMLInputElement>;
  returnFocusRef: RefObject<HTMLInputElement>;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
};

export function MobileAutocompleteModal({ title, input, inputRef, returnFocusRef, onClose, children, footer }: Props) {
  const titleId = useId();
  const t = useTranslations('common');
  const dismissProps = useModalDismissProps();

  return (
    <Modal.Root
      open
      size="fullScreen"
      useDismissProps={{ ...dismissProps, escapeKey: false }}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content
            aria-labelledby={titleId}
            aria-describedby={undefined}
            focusManagerProps={
              { initialFocus: inputRef, returnFocus: returnFocusRef } as ComponentProps<
                typeof Modal.Content
              >['focusManagerProps']
            }
            onKeyDownCapture={(event) => {
              if (event.key === 'Escape' && !event.nativeEvent.isComposing) {
                event.preventDefault();
                event.stopPropagation();
                onClose();
              }
            }}
          >
            <Modal.Header
              css={{
                paddingBlock: 'layout.tight',
                paddingInlineStart: 'layout.tight',
                paddingInlineEnd: 'layout.tight',
              }}
            >
              <span id={titleId} className={css({ srOnly: true })}>
                {title}
              </span>
              <Box css={{ position: 'relative' }}>
                <AdornedInput
                  fluid
                  css={{
                    ...locationPillSurface,
                    paddingInlineEnd:
                      'calc({spacing.default} + {spacing.tightest} + {sizes.action.sm} + {spacing.tighter})',
                  }}
                  startAdornment={
                    <AdornedInput.Adornment>
                      <LucideIcon icon={MapPin} size="md" />
                    </AdornedInput.Adornment>
                  }
                  input={input}
                />
                <CloseButton
                  label={t('close')}
                  onClick={onClose}
                  css={{
                    position: 'absolute',
                    insetBlockStart: '50%',
                    transform: 'translateY(-50%)',
                    insetInlineEnd: 'calc({spacing.default} + {spacing.tightest})',
                  }}
                />
              </Box>
            </Modal.Header>
            <Modal.Body css={{ padding: 'zero', minBlockSize: 'zero', overscrollBehavior: 'contain' }}>
              {children}
            </Modal.Body>
            {footer ? <Modal.Footer>{footer}</Modal.Footer> : null}
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
